import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';
import { TemperatureDataRow } from '../models/temperature-data-row';
import { ZoneDataRow } from '../models/zone-data-row';
import { environment } from './../../environments/environment';
import { ProjectTemperatureData } from '../models/project-temperature-data';
import { ProjectZoneData } from '../models/project-zone-data';
import { ProjectLossData } from '../models/project-loss-data';
import { MarkerInfo } from '../models/marker-info';
import { Note } from '../models/note';
import { ProjectInfo, ProjectInfoResponse } from '../models/project-info';
import { VericureUser } from '../models/vericure-user';
import { UserAuthService } from '../UserAuth.service';
import { PreferencesService } from './preferences.service';

export type DeviceStatus = 'online' | 'offline' | 'unknown';

const LANG_CODE_TO_LABEL: Record<string, string> = {
  en: 'English', nl: 'Dutch', fr: 'French', de: 'German', it: 'Italian', es: 'Spanish',
};

@Injectable({
  providedIn: 'root',
})
export class ProjectService {
  projectList: ProjectInfo[] = [];
  tenandId = 'faa4be5e-a16c-4943-b00e-a8edc85cf171';
  isMetricUnit: boolean = true;
  isHelpOverlayVisible: boolean = false;
  selectedLang: string = '';
  toUpload_isMetricUnit: boolean = true;
  toUpload_selectedLang: string = 'English';
  
  // Per-project unit preferences (in-memory storage)
  private projectUnitPreferences: Map<string, boolean> = new Map();
  private projectLanguagePreferences: Map<string, string> = new Map();
  
  // Marker positioning constants
  private readonly INITIAL_MARKER_POSITION = 10; // meters
  private readonly MINIMUM_MARKER_SPACING = 0.5; // meters
  private readonly MARKER_INCREMENT = 1; // meters
  private samplingInterval: number = 0.15; // Default value, will be updated from API

  private selectedProjectSource = new BehaviorSubject<string>('');
  selectedProject = this.selectedProjectSource.asObservable();

  private uploadedProjectSource = new BehaviorSubject<string>('');
  uploadedProject = this.uploadedProjectSource.asObservable();

  private temperatureDataSource = new BehaviorSubject<ProjectTemperatureData>({
    TRC: [],
    SI: 0,
    MI: 0,
    CRB: 0,
    CRE: 0,
    CRS: 0,
    LSS: 0,
    LES: 0,
    CT: 0,
    OT: 0,
    SER: '',
    FIB: '',
    HMC: [],
    MAR: [],
    N: [],
    U: [],
    LG: '',
    VS: 0,
  });
  temperatureData = this.temperatureDataSource.asObservable();

  private temperatureRTDataSource = new BehaviorSubject<ProjectTemperatureData>(
    {
      TRC: [],
      SI: 0,
      MI: 0,
      CRB: 0,
      CRE: 0,
      CRS: 0,
      LSS: 0,
      LES: 0,
      CT: 0,
      OT: 0,
      SER: '',
      FIB: '',
      HMC: [],
      MAR: [],
      N: [],
      U: [],
      LG: '',
      VS: 0,
    }
  );
  temperatureRTData = this.temperatureRTDataSource.asObservable();

  private lossDataSource = new BehaviorSubject<ProjectLossData>({
    LRC: [],
    SI: 0,
    MI: 0,
    CRB: 0,
    CRE: 0,
    CRS: 0,
    LSS: 0,
    LES: 0,
    CT: 0,
    MIR: false,
  });
  lossData = this.lossDataSource.asObservable();

  private lossRTDataSource = new BehaviorSubject<ProjectLossData>({
    LRC: [],
    SI: 0,
    MI: 0,
    CRB: 0,
    CRE: 0,
    CRS: 0,
    LSS: 0,
    LES: 0,
    CT: 0,
    MIR: false,
  });
  lossRTData = this.lossRTDataSource.asObservable();

  private zoneDataSource = new BehaviorSubject<ProjectZoneData>({
    ZRC: [],
    SI: 0,
    MI: 0,
    CRB: 0,
    CRE: 0,
    CRS: 0,
    LSS: 0,
    LES: 0,
    CT: 0,
  });
  zoneData = this.zoneDataSource.asObservable();

  private zoneRTDataSource = new BehaviorSubject<ProjectZoneData>({
    ZRC: [],
    SI: 0,
    MI: 0,
    CRB: 0,
    CRE: 0,
    CRS: 0,
    LSS: 0,
    LES: 0,
    CT: 0,
  });
  zoneRTData = this.zoneRTDataSource.asObservable();

  private notesDataSource = new BehaviorSubject<Note[]>([]);
  notesData = this.notesDataSource.asObservable();

  private tickPositionSource = new BehaviorSubject<number>(0);
  tickPosition = this.tickPositionSource.asObservable();

  private vert_annot_positionSource = new BehaviorSubject<number>(10);
  vert_annot_position = this.vert_annot_positionSource.asObservable();

  private vert_annot_positionSources = new BehaviorSubject<MarkerInfo[]>([]);
  vert_annot_positions = this.vert_annot_positionSources.asObservable();

  private fiberFaultLevelSource = new BehaviorSubject<number>(0);
  fiberFaultLevel = this.fiberFaultLevelSource.asObservable();

  private cmsFaultLevelSource = new BehaviorSubject<number>(0);
  cmsFaultLevel = this.cmsFaultLevelSource.asObservable();

  private deviceStatusSource = new BehaviorSubject<DeviceStatus>('unknown');
  deviceStatus$ = this.deviceStatusSource.asObservable();
  private lastDataReceivedTime: number = 0;

  constructor(private http: HttpClient, private authService: UserAuthService, private preferencesService: PreferencesService) {
    //this.loadProjectList(this.objectId);
  }

  /**
   * Get the effective tenant ID to use for API calls
   * @param providedTenantId - Optional tenant ID provided by caller
   * @returns The tenant ID to use for API calls
   */
  private getEffectiveTenantId(providedTenantId?: string): string {
    // Priority order:
    // 1. Provided tenant ID (from parameter)
    // 2. Tenant ID from auth service (from login response)
    // 3. Hardcoded fallback tenant ID
    
    if (providedTenantId) {
      return providedTenantId;
    }
    
    const authTenantId = this.authService.getCurrentCustomerTenantId();
    if (authTenantId) {
      return authTenantId;
    }
    
    return this.tenandId; // Fallback to hardcoded value
  }

  loadProjectList(objectId: string, CustomerTenantId?: string) {
    this.getClientIp().subscribe(
      (clientIp) => {
        // Use provided CustomerTenantId or get effective tenant ID
        const effectiveCustomerTenantId = this.getEffectiveTenantId(CustomerTenantId);
        
        const activeCustomerId = this.authService.getActiveCustomerId();
        let params = new HttpParams()
          .set('tenantId', effectiveCustomerTenantId)
          .set('objectId', objectId)
          .set('code', environment.apiKey)
          .set('clientIp', clientIp);
        if (activeCustomerId) {
          params = params.set('customerId', activeCustomerId);
        }
        const options = { params };

        console.log('ProjectService > Loading projects for customer tenant:', effectiveCustomerTenantId, 'customerId:', activeCustomerId);

        this.http
          .get<ProjectInfoResponse[]>(environment.apiUrl + 'GetProjectlist', options)
          .subscribe(
            (data) => {
              console.log('ProjectService > Raw API response:', data);
              console.log('ProjectService > Response type:', typeof data);
              console.log('ProjectService > Is array:', Array.isArray(data));
              
              if (data && Array.isArray(data)) {
                // Convert API response to ProjectInfo instances
                this.projectList = data.map(item => {
                  console.log('ProjectService > Processing item (full object):', JSON.stringify(item));
                  
                  // Handle both old and new API response formats
                  let projectData: ProjectInfoResponse;
                  const anyItem = item as any;
                  
                  // Check which format we have by looking at the actual fields
                  // The API might return: Name/PNAME, SER/SerialNumber, LTR/LastTelemetryReceived
                  const projectName = anyItem.PNAME || anyItem.Name || anyItem.ProjectName;
                  const serialNumber = anyItem.SER || anyItem.SerialNumber || anyItem.DTSSerial;
                  const lastTelemetry = anyItem.LTR || anyItem.LastTelemetryReceived;
                  
                  console.log('ProjectService > Field extraction:');
                  console.log('  - ProjectName:', projectName, 'from', { PNAME: anyItem.PNAME, Name: anyItem.Name, ProjectName: anyItem.ProjectName });
                  console.log('  - SerialNumber:', serialNumber, 'from', { SER: anyItem.SER, SerialNumber: anyItem.SerialNumber, DTSSerial: anyItem.DTSSerial });
                  console.log('  - LastTelemetry:', lastTelemetry, 'from', { LTR: anyItem.LTR, LastTelemetryReceived: anyItem.LastTelemetryReceived });
                  
                  // Decode FiberType if it's base64 encoded
                  let fiberType = anyItem.FiberType || '';
                  if (anyItem.Registry?.FiberType) {
                    try {
                      fiberType = atob(anyItem.Registry.FiberType);
                    } catch (e) {
                      fiberType = anyItem.Registry.FiberType;
                    }
                  }
                  
                  const customerName = anyItem.CustomerName || '';
                  console.log('ProjectService > CustomerName from API:', anyItem.CustomerName, '-> mapped to:', customerName);
                  
                  projectData = {
                    PNAME: projectName,
                    ProjectName: projectName,
                    SER: serialNumber,
                    SerialNumber: serialNumber,
                    LTR: lastTelemetry,
                    LastTelemetryReceived: lastTelemetry,
                    FiberType: fiberType,
                    Version: anyItem.Version || anyItem.MeasuredLinerVersion || '',
                    CustomerName: customerName,
                    UserParameter: anyItem.UserParameter || []
                  };
                  
                  console.log('ProjectService > Mapped projectData (with CustomerName):', projectData);
                  
                  const projectInfo = new ProjectInfo(projectData);
                  console.log('ProjectService > Created ProjectInfo (LTR=' + projectInfo.LTR + ', LastTelemetryReceived=' + projectInfo.LastTelemetryReceived + ')');
                  return projectInfo;
                }).filter((item): item is ProjectInfo => item !== null);
                
                console.log('ProjectService > Final project list:', this.projectList);
              } else {
                console.error('ProjectService > Invalid API response format:', data);
                this.projectList = [];
              }
            },
            (error) => {
              console.error('Error loading project list:', error);
              this.projectList = [];
            }
          );
      },
      (error) => {
        console.error('Error getting client IP:', error);
      }
    );
  }

  getClientIp() {
    const headers = new HttpHeaders().set(
      'Content-Type',
      'text/plain; charset=utf-8'
    );
    const requestOptions: Object = {
      headers: headers,
      responseType: 'text',
    };
    return this.http
      .get<any>('https://api.ipify.org/?format=text', requestOptions)
      .pipe(
        catchError((err) => {
          return '';
        })
      );
  }

  validateProject(projName: string): Observable<number> {
    const options = {
      params: new HttpParams()
        .set('tenantId', this.getEffectiveTenantId())
        .set('projectName', projName)
        .set('code', environment.apiKey),
    };

    var result = this.http.get<number>(
      environment.apiUrl + 'ValidateProjectSubscription',
      options
    );

    return result;
  }

  loadTemperatureData(
    projName: string,
    isRealtime: boolean,
    lastEpocTime: number
  ): Observable<ProjectTemperatureData> {
    const options = {
      params: new HttpParams()
        .set('tenantId', this.getEffectiveTenantId())
        .set('projectName', projName)
        .set('code', environment.apiKey)
        .set('unitCode', this.isMetricUnit ? 0 : 1)
        .set('isRealtime', isRealtime)
        .set('lastEpocTime', lastEpocTime),
    };

    var result = this.http.get<ProjectTemperatureData>(
      environment.apiUrl + 'GetTemperatureData',
      options
    );

    // var result = this.http.get<ProjectTemperatureData>(
    //   'http://localhost:7071/api/ReadNotes'
    // );
    return result;

    // result.subscribe(
    //   (data) => {
    //     this.temperatureDataSource.next(data);
    //     //console.log('Data Received');
    //   },
    //   (error) => console.log(error)
    // );
  }

  setTempData(temp: ProjectTemperatureData) {
    this.temperatureDataSource.next(temp);
    // Store sampling interval for marker positioning
    if (temp.SI) {
      this.samplingInterval = temp.SI;
    }
  }

  setRTTempData(temp: ProjectTemperatureData) {
    this.temperatureRTDataSource.next(temp);
    // Store sampling interval for marker positioning
    if (temp.SI) {
      this.samplingInterval = temp.SI;
    }
  }

  setNotesData(notes: Note[]) {
    this.notesDataSource.next(notes);
  }

  loadZoneData(projName: string) {
    const options = {
      params: new HttpParams()
        .set('tenantId', this.getEffectiveTenantId())
        .set('projectName', projName)
        .set('code', environment.apiKey)
        .set('unitCode', this.isMetricUnit ? 0 : 1)
        .set('isRealtime', false)
        .set('lastEpocTime', 0),
    };

    var result = this.http.get<ProjectZoneData>(
      environment.apiUrl + 'GetHeatmapData',
      options
    );

    result.subscribe(
      (data) => {
        this.zoneDataSource.next(data);
        //console.log('Data Received');
      },
      (error) => console.log(error)
    );
  }

  loadZoneDataRT(projName: string, lastEpocTime: number) {
    const options = {
      params: new HttpParams()
        .set('tenantId', this.getEffectiveTenantId())
        .set('projectName', projName)
        .set('code', environment.apiKey)
        .set('unitCode', this.isMetricUnit ? 0 : 1)
        .set('isRealtime', true)
        .set('lastEpocTime', lastEpocTime),
    };

    var result = this.http.get<ProjectZoneData>(
      environment.apiUrl + 'GetHeatmapData',
      options
    );

    result.subscribe(
      (data) => {
        this.zoneRTDataSource.next(data);
        //console.log('Data Received');
      },
      (error) => console.log(error)
    );
  }

  loadLossData(projName: string) {
    const options = {
      params: new HttpParams()
        .set('tenantId', this.getEffectiveTenantId())
        .set('projectName', projName)
        .set('code', environment.apiKey)
        .set('unitCode', this.isMetricUnit ? 0 : 1)
        .set('language', this.selectedLang)
        .set('isRealtime', false)
        .set('lastEpocTime', 0),
    };

    var result = this.http.get<ProjectLossData>(
      environment.apiUrl + 'GetLossData',
      options
    );

    result.subscribe(
      (data) => {
        this.lossDataSource.next(data);
        //console.log('Data Received');
      },
      (error) => console.log(error)
    );
  }

  loadLossRTData(projName: string, lastEpocTime: number) {
    const options = {
      params: new HttpParams()
        .set('tenantId', this.getEffectiveTenantId())
        .set('projectName', projName)
        .set('code', environment.apiKey)
        .set('unitCode', this.isMetricUnit ? 0 : 1)
        .set('language', this.selectedLang)
        .set('isRealtime', true)
        .set('lastEpocTime', lastEpocTime),
    };

    var result = this.http.get<ProjectLossData>(
      environment.apiUrl + 'GetLossData',
      options
    );

    result.subscribe(
      (data) => {
        this.lossRTDataSource.next(data);
        //console.log('Data Received');
      },
      (error) => console.log(error)
    );
  }

  // Notes are now loaded from GetTemperatureData API response (N property)
  // Removed loadNotesData and loadRealtimeNotesData methods

  uploadProject(fileName: string, content: FormData) {
    const options = {
      params: new HttpParams()
        .set('tenantId', this.getEffectiveTenantId())
        .set('projectName', fileName)
        .set('code', environment.apiKey),
      observe: 'events',
      reportProgress: true,
    };

    var result = this.http.post<any>(
      environment.apiUrl + 'UploadProject',
      content,
      {
        params: new HttpParams()
          .set('tenantId', this.getEffectiveTenantId())
          .set('projectName', fileName)
          .set('code', environment.apiKey),
        observe: 'events',
        reportProgress: true,
      }
    );
    return result;
  }

  startProject(projectName: string) {
    const options = {
      params: new HttpParams()
        .set('tenantId', this.getEffectiveTenantId())
        .set('projectName', projectName)
        .set('code', environment.apiKey),
    };

    var result = this.http.get<string>(
      environment.apiUrl + 'StartProject',
      options
    );
    return result;
  }

  setUploadedProject(projName: string) {
    this.uploadedProjectSource.next(projName);
  }

  getTempData() {
    return this.temperatureDataSource.getValue();
  }

  setTick(position: number) {
    this.tickPositionSource.next(position);
    //console.log('Service > tickPositionSource updated');
  }

  getTick() {
    return this.tickPositionSource.getValue();
  }

  setProject(projName: string) {
    this.selectedProjectSource.next(projName);
    //console.log('Service >  selectedProjectSource updated');
  }

  getProject() {
    return this.selectedProjectSource.getValue();
  }

  setVerticalAnnotationPosition(position: number) {
    this.vert_annot_positionSource.next(position);
  }

  getVerticalAnnotationPosition() {
    return this.vert_annot_positionSource.getValue();
  }

  addMarker() {
    const currentMarkers = this.vert_annot_positionSources.getValue();
    
    // Calculate position using collision detection logic
    const newPosition = this.calculateMarkerPosition(currentMarkers);
    
    // Get color for the new marker using HSL gradient - ensures unique colors
    const totalColors = 18; // Total colors in the gradient
    const colorIndex = currentMarkers.length;
    const rgbColor = this.getColorGradient(colorIndex, totalColors);
    
    console.log(`Adding marker #${colorIndex} with unique color: ${rgbColor} at position: ${newPosition}m`);
    
    const newMarker: MarkerInfo = {
      P: newPosition,
      C: rgbColor
    };
    
    // Add new marker to the list
    const allMarkers = [...currentMarkers, newMarker];
    
    // Get the current project name
    const projectName = this.getProject();
    
    if (!projectName) {
      console.error('No project selected. Cannot add marker.');
      return;
    }
    
    // Prepare the API URL and payload in the format expected by the API
    const apiUrl = `${environment.apiUrl}v1/projects/markers`;
    const params = new HttpParams().set('projectName', projectName);
    
    // Convert to API format: Position and Color instead of P and C
    const apiPayload = allMarkers.map(marker => ({
      Position: marker.P,
      Color: marker.C
    }));
    
    // Call the API with all markers
    console.log('Saving markers to API:', apiUrl);
    console.log('Project Name:', projectName);
    console.log('New marker position (meters):', newPosition);
    console.log('Payload:', apiPayload);
    
    this.http.post(apiUrl, apiPayload, { params }).subscribe(
      (response) => {
        console.log('Markers saved successfully:', response);
        // Update the markers data in the service to reflect the changes
        this.vert_annot_positionSources.next(allMarkers);
      },
      (error) => {
        console.error('Error saving markers:', error);
        alert('Failed to save marker. Please try again.');
      }
    );
  }

  /**
   * Calculate the position for a new marker using collision detection
   * @param existingMarkers Array of existing markers
   * @returns Position in meters for the new marker
   */
  private calculateMarkerPosition(existingMarkers: MarkerInfo[]): number {
    let candidatePosition = this.INITIAL_MARKER_POSITION; // Start at 10 meters
    
    // Convert to samples for collision detection
    let candidateSample = this.metersToSamples(candidatePosition);
    
    // Keep checking positions until we find one without collision
    let attempts = 0;
    const maxAttempts = 1000; // Safety limit to avoid infinite loop
    
    while (attempts < maxAttempts) {
      // Check if this position collides with any existing marker
      const hasCollision = this.checkCollision(candidateSample, existingMarkers);
      
      if (!hasCollision) {
        // No collision found, use this position
        console.log(`Marker position calculated: ${candidatePosition}m (sample: ${candidateSample}), attempts: ${attempts + 1}`);
        return candidatePosition;
      }
      
      // Collision detected, move 1 meter to the right
      candidatePosition += this.MARKER_INCREMENT;
      candidateSample = this.metersToSamples(candidatePosition);
      attempts++;
    }
    
    console.warn('Max attempts reached for marker positioning, using last candidate position');
    return candidatePosition;
  }

  /**
   * Check if a candidate position collides with existing markers
   * @param candidateSample Position in samples
   * @param existingMarkers Array of existing markers
   * @returns true if collision detected, false otherwise
   */
  private checkCollision(candidateSample: number, existingMarkers: MarkerInfo[]): boolean {
    const minSpacingSamples = this.metersToSamples(this.MINIMUM_MARKER_SPACING);
    
    for (const marker of existingMarkers) {
      const markerSample = this.metersToSamples(marker.P);
      const distance = Math.abs(candidateSample - markerSample);
      
      if (distance < minSpacingSamples) {
        return true; // Collision detected
      }
    }
    
    return false; // No collision
  }

  /**
   * Convert meters to samples using the sampling interval
   * @param meters Position in meters
   * @returns Position in samples
   */
  private metersToSamples(meters: number): number {
    if (this.samplingInterval <= 0) {
      console.warn('Invalid sampling interval, using default 0.15m');
      return meters / 0.15;
    }
    return meters / this.samplingInterval;
  }

  /**
   * Convert samples to meters using the sampling interval
   * @param samples Position in samples
   * @returns Position in meters
   */
  private samplesToMeters(samples: number): number {
    if (this.samplingInterval <= 0) {
      console.warn('Invalid sampling interval, using default 0.15m');
      return samples * 0.15;
    }
    return samples * this.samplingInterval;
  }

  loadMarkers(markers: MarkerInfo[]) {
    console.log('ProjectService > loadMarkers called with:', markers);
    console.log('ProjectService > Number of markers:', markers?.length);
    this.vert_annot_positionSources.next(markers);
  }

  getVerticalAnnotationPositions() {
    return this.vert_annot_positionSources.getValue();
  }
  
  private hexToRgb(hex: string): string {
    // Remove # if present
    hex = hex.replace('#', '');
    
    // Parse hex to RGB
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    return `${r}, ${g}, ${b}`;
  }
  
  /**
   * Generate a color using HSL gradient for better color distribution
   * @param index The index of the marker
   * @param totalColors Total number of colors in the gradient (default 18)
   * @returns RGB color string in format "r, g, b"
   */
  private getColorGradient(index: number, totalColors: number = 18): string {
    // Calculate hue based on index to distribute colors evenly around the color wheel
    const hue = (index * 360 / totalColors) % 360;
    const saturation = 100; // Full saturation for vibrant colors
    const lightness = 50;   // Medium lightness for visibility
    
    // Convert HSL to RGB
    const rgb = this.hslToRgb(hue, saturation, lightness);
    
    return `${rgb.r}, ${rgb.g}, ${rgb.b}`;
  }
  
  /**
   * Convert HSL color values to RGB
   * @param h Hue (0-360)
   * @param s Saturation (0-100)
   * @param l Lightness (0-100)
   * @returns RGB object with r, g, b values (0-255)
   */
  private hslToRgb(h: number, s: number, l: number): { r: number, g: number, b: number } {
    // Normalize values
    s = s / 100;
    l = l / 100;
    
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs((h / 60) % 2 - 1));
    const m = l - c / 2;
    
    let r = 0, g = 0, b = 0;
    
    if (h >= 0 && h < 60) {
      r = c; g = x; b = 0;
    } else if (h >= 60 && h < 120) {
      r = x; g = c; b = 0;
    } else if (h >= 120 && h < 180) {
      r = 0; g = c; b = x;
    } else if (h >= 180 && h < 240) {
      r = 0; g = x; b = c;
    } else if (h >= 240 && h < 300) {
      r = x; g = 0; b = c;
    } else if (h >= 300 && h < 360) {
      r = c; g = 0; b = x;
    }
    
    return {
      r: Math.round((r + m) * 255),
      g: Math.round((g + m) * 255),
      b: Math.round((b + m) * 255)
    };
  }
  
  private getMarkerColors(): string[] {
    return [
      '#7F30F3',
      '#63EE55',
      '#F00049',
      '#17A4E5',
      '#C6FA64',
      '#BB12BB',
      '#4BF0C8',
      '#F99230',
      '#2E2CC3',
      '#26C462',
      '#B5146E',
      '#40ECFA',
      '#E7E04F',
    ];
  }

  getMarkerColorForIndex(index: number) {
    const colors = this.getMarkerColors();
    return colors[index % colors.length];
  }

  updateVerticalAnnotationPosition(index: number, position: number) {
    var curPositions = this.vert_annot_positionSources.getValue();

    if (curPositions.length > index) {
      curPositions[index].P = position;
      
      // Get the current project name
      const projectName = this.getProject();
      
      if (!projectName) {
        console.error('No project selected. Cannot update marker position.');
        return;
      }
      
      // Prepare the API URL and payload in the format expected by the API
      const apiUrl = `${environment.apiUrl}v1/projects/markers`;
      const params = new HttpParams().set('projectName', projectName);
      
      // Convert to API format: Position and Color instead of P and C
      const apiPayload = curPositions.map(marker => ({
        Position: marker.P,
        Color: marker.C
      }));
      
      // Call the API to persist all markers with updated positions
      console.log('Updating marker positions to API:', apiUrl);
      console.log('Project Name:', projectName);
      console.log('Updated marker index:', index, 'New position (meters):', position);
      console.log('Payload:', apiPayload);
      
      this.http.post(apiUrl, apiPayload, { params }).subscribe(
        (response) => {
          console.log('Marker positions updated successfully:', response);
          // Don't trigger subscription update here - the marker is already moved by the drag operation
          // Just update the internal state silently
        },
        (error) => {
          console.error('Error updating marker positions:', error);
          alert('Failed to update marker position. Please try again.');
        }
      );
    }
  }

  setFiberFaultLevel(level: number) {
    this.fiberFaultLevelSource.next(level);
  }

  setCmsFaultLevel(level: number) {
    this.cmsFaultLevelSource.next(level);
  }

  setDeviceOnline() {
    this.lastDataReceivedTime = Date.now();
    if (this.deviceStatusSource.value !== 'online') {
      this.deviceStatusSource.next('online');
    }
  }

  checkDeviceTimeout(thresholdMs: number = 30000) {
    if (this.lastDataReceivedTime === 0) return;
    const elapsed = Date.now() - this.lastDataReceivedTime;
    if (elapsed >= thresholdMs && this.deviceStatusSource.value !== 'offline') {
      this.deviceStatusSource.next('offline');
    }
  }

  resetDeviceStatus() {
    this.lastDataReceivedTime = 0;
    this.deviceStatusSource.next('unknown');
  }

  checkUserAuthorization(
    tenantId: string,
    objectId: string
  ): Observable<VericureUser | null> {
    const options = {
      params: new HttpParams()
        .set('tenantId', tenantId)
        .set('objectId', objectId)
        .set('code', environment.apiKey),
    };

    return this.http
      .get<VericureUser>(environment.apiUrl + 'CheckUserExists', options)
      .pipe(catchError(() => of(null)));
  }

  // setIsMetricUnit(val: boolean) {
  //   this.isMetricSource.next(val);
  // }

  unit_Temp_Val(val: number) {
    if (this.isMetricUnit) {
      return val;
    } else {
      return (val * 9) / 5 + 32;
    }
  }
  unit_Temp_Suffix() {
    if (this.isMetricUnit) {
      return '&deg;C';
    } else {
      return '&deg;F';
    }
  }

  formatUnixDateToHumanStringHHMMSS_Custom(unixTimestamp: number) {
    var date;

    //console.log('unix:' + unixTimestamp);
    date = new Date(unixTimestamp);

    //console.log('Curing Date:' + date);
    const hours = date.getUTCHours();
    const minutes = '0' + date.getUTCMinutes();
    const seconds = '0' + date.getUTCSeconds();
    if (isNaN(hours)) {
      return '';
    }

    var formattedTime =
      hours + ':' + minutes.substr(-2) + ':' + seconds.substr(-2);
    //console.log(formattedTime);
    return formattedTime;
  }

  formatDurationInSecToHHMMSS_Custom(durationSec: number) {
    const hours = Math.floor(durationSec / 3600);

    var days = Math.floor(hours / 24);
    var rem_hours = hours % 24;

    const minutes = '0' + Math.floor((durationSec % 3600) / 60);
    const seconds = '0' + Math.floor((durationSec % 3600) % 60);

    if (isNaN(hours)) {
      return '';
    }

    var formattedTime =
      rem_hours + ':' + minutes.substr(-2) + ':' + seconds.substr(-2);
    if (days > 0) {
      return days + '.' + formattedTime;
    } else {
      return formattedTime;
    }
  }

  /**
   * Check if a project has a saved unit preference
   * @param projectName The project name
   * @returns true if preference exists, false otherwise
   */
  hasProjectUnitPreference(projectName: string): boolean {
    return this.projectUnitPreferences.has(projectName);
  }

  /**
   * Get the unit preference for a specific project
   * @param projectName The project name
   * @returns true for metric, false for imperial. Returns default (true) if not set.
   */
  getProjectUnitPreference(projectName: string): boolean {
    if (!projectName) {
      return this.preferencesService.effective$.value.measurementSystem !== 'imperial';
    }
    if (this.projectUnitPreferences.has(projectName)) {
      return this.projectUnitPreferences.get(projectName)!;
    }
    return this.preferencesService.effective$.value.measurementSystem !== 'imperial';
  }

  /**
   * Set the unit preference for a specific project
   * @param projectName The project name
   * @param isMetric true for metric, false for imperial
   */
  setProjectUnitPreference(projectName: string, isMetric: boolean): void {
    if (projectName) {
      this.projectUnitPreferences.set(projectName, isMetric);
      // Update the current unit if this is the active project
      if (this.getProject() === projectName) {
        this.isMetricUnit = isMetric;
        this.toUpload_isMetricUnit = isMetric; // Keep both properties in sync
      }
    }
  }

  /**
   * Check if a project has a saved language preference
   * @param projectName The project name
   * @returns true if preference exists, false otherwise
   */
  hasProjectLanguagePreference(projectName: string): boolean {
    return this.projectLanguagePreferences.has(projectName);
  }

  /**
   * Get the language preference for a specific project
   * @param projectName The project name
   * @returns Language string. Returns default ('English') if not set.
   */
  getProjectLanguagePreference(projectName: string): string {
    const userLangCode = this.preferencesService.effective$.value.language;
    const fallback = LANG_CODE_TO_LABEL[userLangCode] ?? 'English';
    if (!projectName) {
      return fallback;
    }
    return this.projectLanguagePreferences.get(projectName) ?? fallback;
  }

  /**
   * Set the language preference for a specific project
   * @param projectName The project name
   * @param language The language string
   */
  setProjectLanguagePreference(projectName: string, language: string): void {
    if (projectName) {
      this.projectLanguagePreferences.set(projectName, language);
      // Update the current language if this is the active project
      if (this.getProject() === projectName) {
        this.selectedLang = language;
      }
    }
  }
}
